import { isPlatform } from "@ionic/core";

import { Preferences } from "@capacitor/preferences";
import { Device } from "@capacitor/device";

import { IRememberedUser, IScreenProps, IUser } from "../Interfaces";
import DataController from "./DataController";
import RemoteDataController from "./RemoteDataController";

import _ from "lodash";
import { format, addHours, addSeconds } from "date-fns";

export class AuthenticationController {
  async logIn(email: string, password: string, app: IScreenProps["app"]): Promise<null | IUser> {
    return new Promise(async (resolve, reject) => {
      let serverUrl = await RemoteDataController.getAimerEC2URL();
      if (serverUrl === "https://www.aimernginx123.co.uk") {
        // serverUrl was being used for testing temp login - clear it
        Preferences.remove({ key: "serverUrl" });
      }

      if (email === "templogin@class.co.uk") {
        try {
          await Preferences.set({ key: "serverUrl", value: "https://www.aimernginx123.co.uk" });
        } catch (error) {
          console.log(error);
        }
      }

      let url = (await RemoteDataController.getAimerEC2URL()) + "/classapi/SF/login";
      var response = null;

      let product = "";
      if (app === "Plus") {
        product = "PLUS";
      } else if (app === "iCPG") {
        product = "iCPG";
      } else if (app === "CFR") {
        product = "RP";
      }

      try {
        response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            password: password,
            product: product,
          }),
        });
      } catch (error) {
        reject({ serverError: true, errorMessage: error });
      }

      try {
        if (response != null) {
          if (response.status !== 200) {
            reject({ serverError: true, errorMessage: "server error" });
          } else {
            let responseJson = await response.json();

            if (responseJson.auth === true && responseJson.token != null) {
              await this.clearUserData();

              try {
                let user: IUser = {
                  token: responseJson.token,
                  uid: JSON.stringify(responseJson.contactID),
                  email: email,
                  userName: responseJson.userName,
                  subscriptions: [],
                  staffGroups: [],
                  regions: [],
                  trust: "",
                  availableTrusts: [],
                  pushNotificationTopics: [],
                  isTemporary: false,
                  isEPRS: false,
                  isSSO: false,
                };

                user = await this.downloadUserInfo(user, app);

                this.saveUser(user);

                RemoteDataController.logEvent("login", {});

                resolve(user);
              } catch (error) {
                // Error saving data
                console.log("error saving user: " + error);
                reject({ serverError: true, errorMessage: error });
              }
            } else {
              reject({ serverError: false, errorMessage: responseJson.body.ErrorMessage });
            }
          }
        } else {
          reject({ serverError: true, errorMessage: "The server didn't respond" });
        }
      } catch (error) {
        reject({ serverError: true, errorMessage: error });
      }
    });
  }

  async logInWithUserIDAndEmail(email: string, userID: string, app: IScreenProps["app"]): Promise<null | IUser> {
    return new Promise(async (resolve, reject) => {
      let url = (await RemoteDataController.getAimerEC2URL()) + "/classapi/SF/exchange";
      var response = null;

      try {
        response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            ContactID: userID + "",
          }),
        });
      } catch (error) {
        reject({ serverError: true, errorMessage: error });
      }

      try {
        if (response != null) {
          let responseJson = await response.json();

          if (responseJson.success === true && responseJson.token != null) {
            try {
              let user: IUser = {
                token: responseJson.token,
                uid: userID + "",
                email: email,
                userName: "",
                subscriptions: [],
                staffGroups: [],
                regions: [],
                trust: "",
                availableTrusts: [],
                pushNotificationTopics: [],
                isTemporary: false,
                isEPRS: false,
                isSSO: false,
              };

              user = await this.downloadUserInfo(user, app);

              this.saveUser(user);

              resolve(user);
            } catch (error) {
              // Error saving data
              console.log("error saving user: " + error);
              reject({ serverError: true, errorMessage: error });
            }
          } else {
            reject({ serverError: true, errorMessage: responseJson.body.ErrorMessage });
          }
        } else {
          reject({ serverError: true, errorMessage: "The server didn't respond" });
        }
      } catch (error) {
        reject({ serverError: true, errorMessage: error });
      }
    });
  }

  async downloadSSOConfiguration(trust: string, app: IScreenProps["app"]): Promise<{ [key: string]: any }> {
    try {
      if (trust == null || trust === "") {
        return {};
      }

      let url =
        app === "CFR"
          ? `https://aace.s3.amazonaws.com/${trust}/sso.cfr.${trust}.json`
          : `https://aace.s3.amazonaws.com/${trust}/sso.${trust}.json`;

      if (isPlatform("desktop")) {
        if (window.location.origin === "https://jrcalcplusweb-sso-test.netlify.app") {
          url = `https://aace.s3.amazonaws.com/${trust}/sso.${trust}.TEST.json`;
        } else if (window.location.origin === "https://responderplus-sso-test.netlify.app") {
          url = `https://aace.s3.amazonaws.com/${trust}/sso.cfr.${trust}.TEST.json`;
        }
      }

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let result = await response.json();

      if (result != null) {
        return result;
      }
    } catch (error) {
      console.log("error downloading SSO configuration: " + error);
    }

    return {};
  }

  async logInSSO(accessToken: string, trust: string, app: IScreenProps["app"]): Promise<null | IUser> {
    return new Promise(async (resolve, reject) => {
      let url = (await RemoteDataController.getAimerEC2URL()) + "/classapi/SF/loginSSO";
      // url = "http://10.0.25.162:3011/classapi/SF/loginSSO";
      var response = null;

      try {
        response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            trust: trust,
            accessToken: accessToken,
          }),
        });
      } catch (error) {
        reject({ serverError: true, errorMessage: error });
      }

      try {
        if (response != null) {
          let responseJson = await response.json();

          if (responseJson.success === true && responseJson.token != null) {
            try {
              let user: IUser = {
                token: responseJson.token,
                uid: responseJson.contactID,
                email: responseJson.email,
                userName: "",
                subscriptions: [],
                staffGroups: [],
                regions: [],
                trust: "",
                availableTrusts: [],
                pushNotificationTopics: [],
                isTemporary: false,
                isEPRS: false,
                isSSO: true,
              };

              user = await this.downloadUserInfo(user, app);

              this.saveUser(user);

              RemoteDataController.logEvent("loginSSO", {});

              resolve(user);
            } catch (error) {
              // Error saving data
              console.log("error saving user: " + error);
              reject({ serverError: true, errorMessage: error });
            }
          } else {
            reject({ serverError: true, errorMessage: responseJson.error.ErrorMessage });
          }
        } else {
          reject({ serverError: true, errorMessage: "The server didn't respond" });
        }
      } catch (error) {
        reject({ serverError: true, errorMessage: error });
      }
    });
  }

  async logInAsDesktopUser(
    userName: string,
    trust: string,
    onlineLookup: boolean,
    app: IScreenProps["app"]
  ): Promise<null | IUser> {
    return new Promise(async (resolve, reject) => {
      let onlineLookupCompleted = false;

      if (onlineLookup) {
        let url = (await RemoteDataController.getAimerEC2URL()) + "/classapi/SF/desktopUser";
        var response = null;

        try {
          response = await fetch(url, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              username: userName,
              APIKey: "aZ7BejmJ7edYmhfqiyAcWg8VAhLC8m",
            }),
          });
        } catch (error) {
          reject({ serverError: true, errorMessage: error });
        }

        try {
          if (response != null) {
            let responseJson = await response.json();

            if (responseJson.success === true && responseJson.token != null) {
              try {
                let user: IUser = {
                  token: responseJson.token,
                  uid: responseJson.contactID + "",
                  email: "",
                  userName: "",
                  subscriptions: [],
                  staffGroups: [],
                  regions: [],
                  trust: "",
                  availableTrusts: [],
                  pushNotificationTopics: [],
                  isTemporary: false,
                  isEPRS: false,
                  isSSO: false,
                };

                user = await this.downloadUserInfo(user, app);

                this.saveUser(user);

                onlineLookupCompleted = true;

                resolve(user);
              } catch (error) {
                // Error saving data
                console.log("error saving desktop user: " + error);
                reject({ serverError: true, errorMessage: error });
              }
            } else {
              reject({ serverError: true, failedDesktopLogin: true, errorMessage: responseJson.error });
              return;
            }
          } else {
            // carry on to regular EPRS login
          }
        } catch (error) {
          // carry on to regular EPRS login
        }
      } else {
      }

      if (onlineLookup === false || onlineLookupCompleted === false) {
        let user: IUser = {
          token: "",
          uid: null,
          email: userName,
          userName: userName,
          subscriptions: [],
          staffGroups: [],
          regions: [],
          trust: trust,
          availableTrusts: [trust],
          pushNotificationTopics: [],
          isTemporary: false,
          isEPRS: true,
          isSSO: false,
        };

        this.saveUser(user);

        resolve(user);
      }
    });
  }

  async logInTemporarily(trust: string): Promise<IUser> {
    return new Promise(async (resolve, reject) => {
      try {
        let oneHourAhead = addHours(Date.now(), 1);

        let user: IUser = {
          token: "",
          uid: null,
          email: "Temporary Login",
          userName: "Temporary Login",
          subscriptions: [],
          staffGroups: [],
          regions: [],
          trust: trust,
          availableTrusts: [trust],
          pushNotificationTopics: [],
          isTemporary: true,
          temporaryLoginTime: oneHourAhead.getTime(),
          isEPRS: false,
          isSSO: false,
        };

        resolve(user);
      } catch (error) {
        // Error saving data
        console.log("error with temporary user: " + error);
        reject({ serverError: true, errorMessage: error });
      }
    });
  }

  getUserForRememberedUser(rememberedUser: IRememberedUser, trust: string, app: IScreenProps["app"]): Promise<IUser> {
    return new Promise(async (resolve, reject) => {
      try {
        let availableTrusts: string[] = [];
        if (rememberedUser.availableTrusts) {
          availableTrusts = rememberedUser.availableTrusts;
        }

        let user: IUser = {
          token: rememberedUser.token,
          uid: rememberedUser.uid,
          email: rememberedUser.email,
          userName: "",
          subscriptions: [],
          staffGroups: [],
          regions: [],
          trust: trust,
          availableTrusts: availableTrusts,
          pushNotificationTopics: [],
          isTemporary: false,
          isEPRS: false,
          isSSO: rememberedUser.isSSO ? rememberedUser.isSSO : false,
        };

        user = await this.downloadUserInfo(user, app);

        this.saveUser(user);

        resolve(user);
      } catch (error) {
        // Error saving data
        console.log("error getting user for remembered user: " + error);
        reject(error);
      }
    });
  }

  async clearUserData() {
    // all local storage will be cleared except for the keys specified here
    let storageKeysToKeep = [
      "localUsageLog",
      "rememberedUsers",
      "significantChanges",
      "serverUrl",
      "ssoTrusts",
      "showOlark",
      "finishedOnboarding",
      "customiseDashboardCardDismissed",
      "notificationBannerDismissed",
      "bookmarkSuggestionDismissed",
      "disclaimerDismissed",
      "cfrDisclaimerDismissed",
      "subscribedPushNotificationTopics",
      "fontSize",
      "lineHeight",
      "fontFamily",
      "showTextToSpeech",
      "showColorFilter",
      "freezeFirstDrugColumn",
      "keepScreenAwake",
      "textToSpeechSpeed",
      "fontWeight",
      "accessabilityUnderlineLinks",
      "hideParaPassLinks",
      "appearance",
      "abTestingVersion",
      "debugMode",
    ];

    try {
      let storageKeys = await Preferences.keys();
      for (let key of storageKeys.keys) {
        if (storageKeysToKeep.includes(key) === false && key.includes("appNoticesCardDismissed") === false) {
          await Preferences.remove({ key: key });
        }
      }
      let serverUrl = await RemoteDataController.getAimerEC2URL();
      if (serverUrl === "https://www.aimernginx123.co.uk") {
        // serverUrl was being used for testing temp login - clear it
        Preferences.remove({ key: "serverUrl" });
      }
    } catch (error) {
      // Error saving data
      console.log("error removing items: " + error);
    }
  }

  async registerUser(
    emailAddress: string,
    password: string,
    firstName: string,
    lastName: string,
    app: IScreenProps["app"]
  ) {
    return new Promise(async (resolve, reject) => {
      let url = (await RemoteDataController.getAimerEC2URL()) + "/classapi/SF/register";
      var response = null;

      let product = "";
      if (app === "iCPG") {
        product = "iCPG";
      } else if (app === "CFR") {
        product = "CFR Plus";
      }

      try {
        response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: emailAddress,
            password: password,
            firstName: firstName,
            lastName: lastName,
            CreatedSubscriberProduct: product,
          }),
        });
      } catch (error) {
        reject({ serverError: true, errorMessage: error });
      }

      if (response != null) {
        var responseJson = await response.json();

        if (responseJson) {
          if (responseJson.success === true) {
            resolve(true);
          } else {
            if (responseJson.body.ErrorMessage === "Failed Validation") {
              var validationList = responseJson.body.ValidationList;

              if (validationList != null && validationList.length > 0) {
                var firstValidationRule = validationList[0];
                reject({ serverError: false, errorMessage: firstValidationRule.Message });
              } else {
                reject({ serverError: false, errorMessage: "Failed Validation" });
              }
            } else {
              reject({ serverError: false, errorMessage: responseJson.body.ErrorMessage });
            }
          }
        } else {
          reject({ serverError: true, errorMessage: "Server didn't respond" });
        }
      } else {
        reject({ serverError: true, errorMessage: "The server didn't respond" });
      }
    });
  }

  async downloadUserInfo(user: IUser, app: IScreenProps["app"]) {
    return new Promise<IUser>(async (resolve, reject) => {
      try {
        if (user.uid == null) {
          reject("Temporary user");
          return;
        }

        let url = (await RemoteDataController.getAimerEC2URL()) + "/classapi/SF/userInfo";

        if (user.token == null || user.token === "") {
          console.log("Error getting items: No token provided");
          reject("No token provided");
        }

        let deviceID = "";

        try {
          let uuid = await Device.getId();
          deviceID = uuid.identifier;
        } catch (error) {
          console.log(error);
        }

        let product = "";
        if (app === "Plus") {
          product = "PLUS";
        } else if (app === "iCPG") {
          product = "iCPG";
        } else if (app === "CFR") {
          product = "RP";
        }

        var response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ContactID: user.uid + "",
            token: user.token,
            deviceID: deviceID,
            product: product,
          }),
        });

        if (response.status === 401) {
          await this.clearUserData();
          reject("unauthorised");
          return;
        }

        let responseJson = await response.json();
        if (response.status === 200 && responseJson.success === true) {
          try {
            let subscriptions = responseJson.subscriptions;
            let trust = "";
            let setUserTrust = false;
            let availableTrusts: string[] = [];
            let pushNotificationTopics: string[] = [];

            try {
              if (responseJson.pushNotificationTopics != null) {
                pushNotificationTopics.concat(responseJson.pushNotificationTopics);
              }
            } catch (error) {}

            if (subscriptions != null && subscriptions.length > 0) {
              for (let subscriptionIndex = 0; subscriptionIndex < subscriptions.length; subscriptionIndex++) {
                let subscription = subscriptions[subscriptionIndex];
                if (subscription.OTA_DEBUG === "true") {
                  user.OTA_DEBUG = true;
                }

                trust = DataController.getTrustFromSubscription(subscription);
                let subscriptionIsForApp = DataController.isSubscriptionForApp(subscription, app);

                let trustIsForApp = false;

                if (app === "Plus") {
                  if (subscription.SubscriptionID !== 1186) {
                    trustIsForApp = true;
                  }
                } else if (app === "CFR") {
                  if (subscription.SubscriptionID === 1186) {
                    trustIsForApp = true;
                  }
                }

                if (subscriptionIsForApp && trustIsForApp) {
                  if (trust !== "" && trust !== "JRCALC") {
                    if (user.trust === "" || trust === user.trust) {
                      user.trust = trust;
                      if (subscription.staffGroup) {
                        user.staffGroups = subscription.staffGroup;
                      }
                      if (subscription.region && subscription.region != null) {
                        user.regions = subscription.region.split(";");
                      } else {
                        user.regions = [];
                      }
                    }
                    if (availableTrusts.includes(trust) === false) {
                      availableTrusts.push(trust);
                    }
                    setUserTrust = true;
                  }
                }

                if (subscriptionIsForApp) {
                  if (subscription.pushNotificationTopics != null) {
                    try {
                      pushNotificationTopics = pushNotificationTopics.concat(subscription.pushNotificationTopics);
                    } catch (error) {}
                  }
                }

                let test = "";
              }

              if (setUserTrust === false) {
                // no subscriptions were for a trust (or were trust sub in iCPG)
                user.trust = "";
              }
            } else {
              subscriptions = [];
            }

            // add push notification topics that always exist
            pushNotificationTopics.push("allUsers");
            // pushNotificationTopics.push('classTestingInternal');
            // pushNotificationTopics.push('AimerTesting');

            // add push notification topics for each available trusts (based on their subscriptions)
            if (availableTrusts != null) {
              for (
                let availableTrustsIndex = 0;
                availableTrustsIndex < availableTrusts.length;
                availableTrustsIndex++
              ) {
                let availableTrust = availableTrusts[availableTrustsIndex];
                if (pushNotificationTopics.includes(availableTrust) === false) {
                  pushNotificationTopics.push(availableTrust);
                }
              }
            }

            user.subscriptions = subscriptions;
            user.availableTrusts = availableTrusts;
            user.pushNotificationTopics = pushNotificationTopics;

            if (responseJson.userName != null) {
              user.userName = responseJson.userName;
            }

            if (responseJson.userLoginName != null) {
              user.email = responseJson.userLoginName;
            }

            this.saveUser(user);
          } catch (error) {
            console.log("Failed to save subscriptions: ", error);
          }
        }

        resolve(user);
      } catch (error) {
        console.log(error);
        reject(null);
      }
    });
  }

  async requestAccountDeletion(user: IUser, app: IScreenProps["app"]) {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        if (user.uid == null) {
          reject("Temporary user");
          return;
        }

        let url = (await RemoteDataController.getAimerEC2URL()) + "/classapi/SF/requestAccountDel";

        if (user.token == null || user.token === "") {
          console.log("Error getting items: No token provided");
          reject("No token provided");
        }

        var response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ContactID: user.uid + "",
            token: user.token,
            product: app,
          }),
        });

        if (response.status === 401) {
          await this.clearUserData();
          reject("unauthorised");
          return;
        }

        let responseJson = await response.json();
        if (response.status === 200 && responseJson.success === true) {
          resolve(true);
        } else {
          resolve(false);
        }
      } catch (error) {
        console.log(error);
        reject(null);
      }
    });
  }

  async getUser(): Promise<null | IUser> {
    try {
      let user: any = await Preferences.get({ key: "user" });

      if (user.value === null) {
        return null;
      }

      return JSON.parse(user.value);
    } catch (error) {
      console.log(error);
    }

    return null;
  }

  async saveUser(user: IUser) {
    // save the current user

    if (user.uid != null) {
      try {
        await Preferences.set({ key: "user", value: JSON.stringify(user) });
      } catch (error) {
        console.log(error);
      }

      // save the user to the remembered users list
      let rememberedUserToSave: IRememberedUser = {
        token: user.token,
        uid: user.uid,
        email: user.email,
        trust: user.trust,
        availableTrusts: user.availableTrusts,
        isSSO: user.isSSO,
      };

      let rememberedUsers = await this.getRememberedUsers();
      // Replace existing remembered user if found
      var index = _.findIndex(rememberedUsers, { uid: user.uid });
      if (index === -1) {
        rememberedUsers.unshift(rememberedUserToSave);
      } else {
        rememberedUsers.splice(index, 1, rememberedUserToSave);
      }

      try {
        await Preferences.set({ key: "rememberedUsers", value: JSON.stringify(rememberedUsers) });
      } catch (error) {
        console.log(error);
      }
    }
  }

  async getRememberedUsers(): Promise<IRememberedUser[]> {
    try {
      let rememberedUsersData: any = await Preferences.get({ key: "rememberedUsers" });

      if (rememberedUsersData.value == null) {
        return [];
      }

      let rememberedUsers = JSON.parse(rememberedUsersData.value);
      rememberedUsers = _.orderBy(rememberedUsers, ["email"], ["asc"]);

      for (let rememberedUser of rememberedUsers) {
        if (rememberedUser.availableTrusts == null) {
          rememberedUser.availableTrusts = [];
        }
      }

      return rememberedUsers;
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async removeRememberedUser(user: IUser | IRememberedUser) {
    if (user.uid != null) {
      let rememberedUsers = await this.getRememberedUsers();
      // Replace existing remembered user if found
      var index = _.findIndex(rememberedUsers, { uid: user.uid });
      if (index !== -1) {
        rememberedUsers.splice(index, 1);
      }

      try {
        await Preferences.set({ key: "rememberedUsers", value: JSON.stringify(rememberedUsers) });
      } catch (error) {
        console.log(error);
      }
    }
  }
}

export default new AuthenticationController();
